import { CommonModule } from '@angular/common';
import { Component, inject, effect, signal } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { COMPANY_INFO } from '../../state/app.selector';
import { CONSTANTS_DATA } from 'paylink-constants';
import { CompanyInfoStoreInterface, LANGUAGE } from 'models';
import { SELECT_LANGUAGE } from '../../state/actions/company.action';
import { MultilingualNamePipe } from '../../features/pipes/multilingual-name.pipe';

@Component({
  standalone: true,
  selector: 'company-not-found',
  templateUrl: './company-not-found.component.html',
  styleUrl: './company-not-found.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    MultilingualNamePipe,
  ],
})
export class CompanyNotFoundComponent {
  public store = inject(Store);
  public translate = inject(TranslateService);
  public router = inject(Router);
  private route = inject(ActivatedRoute);

  // Convert observables to signals at class level
  private routeParams = toSignal(this.route.params, {
    initialValue: {} as Record<string, string>,
  });
  private companyInfoSignal = toSignal(this.store.select(COMPANY_INFO), {
    initialValue: null,
  });

  languages = new BehaviorSubject<any[]>([]);
  languageFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  errorCode = signal<string>('');
  companyDetails = signal<any>(null);

  constructor() {
    this.languageFormGroup = new UntypedFormGroup({
      selectedLang: new UntypedFormControl(''),
    });
    this.languageFormGroup.updateValueAndValidity();

    // Handle route params
    effect(() => {
      const params = this.routeParams();
      if (params?.['errcode']) {
        this.errorCode.set(params['errcode']);
      } else {
        this.errorCode.set('');
      }
    });

    // Initialize and handle company info
    this.store.dispatch({ type: 'FETCHED_COMPANY_DETAILS' });

    effect(() => {
      const company = this.companyInfoSignal();
      if (company) {
        this.companyDetails.set(company.compDetails);
        this.translate.setDefaultLang(company?.selectedLang?.LANG_CODE);
        this.translate.use(company?.selectedLang?.LANG_CODE);
        this.languageFormGroup.patchValue({
          selectedLang: company.selectedLang,
        });
        this.parseSupportedLang(company);
      }
    });
  }

  parseSupportedLang(
    companyDetails: CompanyInfoStoreInterface | undefined,
  ): any {
    let supportedLangBuffer: Array<LANGUAGE> = [];
    let supportedLang: any = [];
    if (companyDetails?.compDetails.supported_languages) {
      supportedLang = companyDetails?.compDetails.supported_languages.map(
        (l: any) => l.trim().toLowerCase(),
      );
    }

    supportedLangBuffer = CONSTANTS_DATA.ALL_LANGUAGES.filter((lang) => {
      const languageOnlower = lang.LANG_ABBRE.toLowerCase();
      const langCodeOnlower = lang.LANG_CODE.toLowerCase();
      if (supportedLang && supportedLang.length > 0) {
        return (
          supportedLang &&
          (supportedLang.includes(languageOnlower) ||
            supportedLang.includes(langCodeOnlower))
        );
      } else if (
        companyDetails?.compDetails.primary_language == languageOnlower
      ) {
        return lang;
      } else {
        return { COUNTRY: 'us', LANG_CODE: 'en', LANG_ABBRE: 'ENGLISH' };
      }
    });
    this.languages.next(supportedLangBuffer);
  }

  onSelectLang(e: LANGUAGE) {
    localStorage.setItem('DEFAULT_LANGUAGE', JSON.stringify(e));
    this.store.dispatch(SELECT_LANGUAGE({ lang: e }));
  }

  get SelectedLang() {
    return this.languageFormGroup.get('selectedLang')!.value;
  }
}
